import React, { useState, useRef } from 'react';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import { InputUpload } from '@ifca-root/react-component/src/components/Input/InputUpload';
import { Button, List, ListItem, ListItemText, ListItemIcon, Typography, Divider, FormControl, InputLabel, Select, MenuItem, Grid } from '@material-ui/core';
import { CloudDownload, CloudUpload, Error as ErrorIcon, CheckCircle } from '@material-ui/icons';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { useImportOpeningBalanceMutation, useHasOpeningBalanceImportedQuery, StockImportMode } from 'generated/graphql';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import { useContext } from 'react';
import { SystemMsgs } from 'helpers/SystemMsg';

interface ImportExportDialogProps {
	open: boolean;
	onClose: () => void;
	outletID: string;
	currentTab?: number; // 0: Stock Balance, 1: Stock Transaction, 2: Sold Out
}

interface ValidationError {
	row: number;
	errors: string[];
}

// Helper function to convert Excel date serial number to ISO date string (YYYY-MM-DD) in local timezone
const convertExcelDateToISO = (value: any): string => {
	// If it's already a string, try to parse it
	if (typeof value === 'string') {
		// Try parsing as YYYY-MM-DD first
		if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
			return value; // Already in correct format
		}
		// Try parsing as DD/MM/YYYY or other formats
		const date = new Date(value);
		if (!isNaN(date.getTime())) {
			// Format as YYYY-MM-DD in local timezone (not UTC)
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		}
		return value; // Return as-is if not a valid date string
	}

	// If it's a number, it's likely an Excel date serial number
	if (typeof value === 'number') {
		// Excel epoch: January 1, 1900 (serial number 1)
		// Excel incorrectly treats 1900 as a leap year, so we use December 30, 1899 as the base
		// JavaScript epoch: January 1, 1970
		// Days between Excel epoch (Dec 30, 1899) and JS epoch (Jan 1, 1970) = 25569
		const excelEpochOffset = 25569; // Days between 1899-12-30 and 1970-01-01
		const millisecondsPerDay = 86400000;
		
		// Convert Excel serial to JavaScript timestamp
		const jsTimestamp = (value - excelEpochOffset) * millisecondsPerDay;
		const jsDate = new Date(jsTimestamp);
		
		if (!isNaN(jsDate.getTime())) {
			// Format as YYYY-MM-DD in local timezone (not UTC)
			const year = jsDate.getFullYear();
			const month = String(jsDate.getMonth() + 1).padStart(2, '0');
			const day = String(jsDate.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		}
	}

	// If it's a Date object, format in local timezone
	if (value instanceof Date) {
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, '0');
		const day = String(value.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	// Fallback: try to convert to string and parse
	return String(value);
};

export const ImportExportDialog: React.FC<ImportExportDialogProps> = ({
	open,
	onClose,
	outletID,
	currentTab = 0, // Default to Stock Balance tab
}) => {
	const { setOpenSnackBar, setSnackBarMsg } = useContext(SnackBarContext as any);
	const [uploadFile, setUploadFile] = useState<string>('');
	const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
	const [isValidating, setIsValidating] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [parsedData, setParsedData] = useState<any[]>([]);
	const [importMode, setImportMode] = useState<StockImportMode>(StockImportMode.OpeningBalance);

	// Check if opening balance has already been imported
	const { data: importStatusData, loading: importStatusLoading } = useHasOpeningBalanceImportedQuery({
		variables: { outletID },
		fetchPolicy: 'network-only',
		skip: !open, // Only fetch when dialog is open
	});

	const hasImported = importStatusData?.hasOpeningBalanceImported || false;

	// Determine which queries to refetch based on current tab
	const getRefetchQueries = () => {
		const baseQueries = [
			'hasOpeningBalanceImported', // Always refetch import status
		];

		// Add queries based on current tab
		if (currentTab === 0) {
			// Stock Balance tab
			baseQueries.push('getStockBalance');
		} else if (currentTab === 1) {
			// Stock Transaction tab
			baseQueries.push('getStockTransactions');
		} else if (currentTab === 2) {
			// Sold Out tab
			baseQueries.push('getSoldOutAggregated');
		}

		// Always refetch stock balance as it affects all tabs
		// Also refetch stock transactions as import creates transactions
		if (!baseQueries.includes('getStockBalance')) {
			baseQueries.push('getStockBalance');
		}
		if (!baseQueries.includes('getStockTransactions')) {
			baseQueries.push('getStockTransactions');
		}

		return baseQueries;
	};

	const [importOpeningBalance, { loading: importLoading }] = useImportOpeningBalanceMutation({
		refetchQueries: getRefetchQueries(),
		onError: (error) => {
			setOpenSnackBar(true);
			setSnackBarMsg(error.message || `Failed to import ${importMode === StockImportMode.OpeningBalance ? 'opening balance' : importMode === StockImportMode.StockIn ? 'stock in' : 'stock out'}`);
			setIsImporting(false);
		},
	});

	const handleClose = () => {
		setUploadFile('');
		setValidationErrors([]);
		setParsedData([]);
		setIsValidating(false);
		setIsImporting(false);
		setImportMode(StockImportMode.OpeningBalance);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
		onClose();
	};

	const handleDownloadTemplate = () => {
		let template: any[] = [];
		let fileName = '';

		if (importMode === StockImportMode.OpeningBalance) {
			template = [
				{
					outletCode: 'OUTLET-001',
					trxDate: '2025-01-01',
					docRef: 'OPEN-2025-001',
					description: 'Opening balance for new outlet',
					menuItemName: 'Sample Menu Item',
					qtyReceived: 100.0000,
					uomCode: 'KG',
					remark: 'Opening stock',
				},
			];
			fileName = 'Opening-Balance-Template.xlsx';
		} else if (importMode === StockImportMode.StockIn) {
			template = [
				{
					outletCode: 'OUTLET-001',
					trxDate: '2025-01-01',
					docRef: 'STOCKIN-2025-001',
					docNo: 'DOC12345',
					description: 'Stock In Import',
					menuItemName: 'Sample Menu Item',
					qtyReceived: 50.0000,
					uomCode: 'KG',
					remark: 'Stock received',
				},
			];
			fileName = 'Stock-In-Template.xlsx';
		} else if (importMode === StockImportMode.StockOut) {
			template = [
				{
					outletCode: 'OUTLET-001',
					trxDate: '2025-01-01',
					docRef: 'STOCKOUT-2025-001',
					docNo: 'DOC12345',
					description: 'Stock Out Import',
					toWarehouseCode: 'WAREHOUSE-002',
					menuItemName: 'Sample Menu Item',
					transferQty: 20.0000,
					uomCode: 'KG',
					reasonCode: 'Damage',
					remark: 'Stock transferred',
				},
			];
			fileName = 'Stock-Out-Template.xlsx';
		}

		const fileType =
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
		const ws = XLSX.utils.json_to_sheet(template);
		const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
		const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const data = new Blob([excelBuffer], { type: fileType });
		FileSaver.saveAs(data, fileName);
	};

	const handleFileSelect = (event: any) => {
		const file: File = event.target.files[0];
		if (!file) return;

		// Validate file type
		const isValid =
			file.type === 'application/vnd.ms-excel' ||
			file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
			file.name.endsWith('.csv');

		if (!isValid) {
			setOpenSnackBar(true);
			setSnackBarMsg('Invalid file type! Please upload .xlsx, .xls, or .csv file');
			return;
		}

		setUploadFile(file.name);
		setValidationErrors([]);
		setIsValidating(true);

		// Read file
		const reader = new FileReader();
		reader.onload = (e: any) => {
			try {
				const data = e.target.result;
				let workbook: XLSX.WorkBook;

				if (file.name.endsWith('.csv')) {
					// Handle CSV - read as text and parse
					const text = data as string;
					const lines = text.split('\n').filter(line => line.trim());
					if (lines.length === 0) {
						throw new Error('CSV file is empty');
					}
					// Parse CSV manually
					const rows: any[] = [];
					const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
					for (let i = 1; i < lines.length; i++) {
						const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
						const row: any = {};
						headers.forEach((header, idx) => {
							row[header] = values[idx] || '';
						});
						rows.push(row);
					}
					// Convert to Excel-like structure
					const ws = XLSX.utils.json_to_sheet(rows);
					workbook = XLSX.utils.book_new();
					XLSX.utils.book_append_sheet(workbook, ws, 'data');
				} else {
					// Handle Excel - use cellDates option to parse dates properly
					workbook = XLSX.read(data, { type: 'array', cellDates: true });
				}

				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const rows: any[] = XLSX.utils.sheet_to_json(worksheet);

				if (rows.length === 0) {
					setOpenSnackBar(true);
					setSnackBarMsg('Excel file is empty');
					setIsValidating(false);
					return;
				}

				// Validate data
				const errors: ValidationError[] = [];
				const validData: any[] = [];

				rows.forEach((row, index) => {
					const rowNum = index + 2; // +2 because Excel rows start at 1 and we have header
					const rowErrors: string[] = [];

					// Validate required fields
					if (!row.outletCode) rowErrors.push('outletCode is required');
					if (!row.trxDate) rowErrors.push('trxDate is required');
					if (!row.menuItemName) rowErrors.push('menuItemName is required');
					
					// Validate quantity based on mode (handled in backend, just basic check here)
					if (importMode === StockImportMode.StockOut) {
						if (row.transferQty === undefined || row.transferQty === null) {
							rowErrors.push('transferQty is required for Stock Out');
						}
					} else {
						if (row.qtyReceived === undefined || row.qtyReceived === null) {
							rowErrors.push('qtyReceived is required');
						}
					}

					// Validate availableQty (if provided)
					if (row.availableQty !== undefined && row.availableQty !== null) {
						const availableQty = parseFloat(String(row.availableQty));
						if (isNaN(availableQty) || availableQty < 0) {
							rowErrors.push(`Invalid availableQty: ${row.availableQty}`);
						} else {
							// availableQty cannot exceed qtyReceived (which becomes stockBalanceQty)
							const qtyReceived = parseFloat(String(row.qtyReceived || 0));
							if (availableQty > qtyReceived) {
								rowErrors.push(`availableQty (${availableQty}) cannot exceed qtyReceived (${qtyReceived})`);
							}
						}
					}

					// Convert and validate date format
					let convertedTrxDate: string | null = null;
					if (row.trxDate) {
						try {
							convertedTrxDate = convertExcelDateToISO(row.trxDate);
							const date = new Date(convertedTrxDate);
							if (isNaN(date.getTime())) {
								rowErrors.push(`Invalid trxDate format: ${row.trxDate}`);
							}
						} catch (error) {
							rowErrors.push(`Invalid trxDate format: ${row.trxDate}`);
						}
					}

					if (rowErrors.length > 0) {
						errors.push({ row: rowNum, errors: rowErrors });
					} else {
						// Create a new row object with converted date
						validData.push({
							...row,
							trxDate: convertedTrxDate || row.trxDate,
						});
					}
				});

				setValidationErrors(errors);
				setParsedData(validData);
				setIsValidating(false);

				if (errors.length > 0) {
					setOpenSnackBar(true);
					setSnackBarMsg(`Found ${errors.length} validation error(s). Please fix and try again.`);
				} else {
					setOpenSnackBar(true);
					setSnackBarMsg(`File validated successfully! ${validData.length} row(s) ready to import.`);
				}
			} catch (error: any) {
				setOpenSnackBar(true);
				setSnackBarMsg(`Error reading file: ${error.message}`);
				setIsValidating(false);
			}
		};

		if (file.name.endsWith('.csv')) {
			reader.readAsText(file);
		} else {
			reader.readAsArrayBuffer(file);
		}
	};

	const handleImport = async () => {
		if (validationErrors.length > 0) {
			setOpenSnackBar(true);
			setSnackBarMsg('Please fix validation errors before importing');
			return;
		}

		if (parsedData.length === 0) {
			setOpenSnackBar(true);
			setSnackBarMsg('No valid data to import');
			return;
		}

		setIsImporting(true);
		try {
			const result = await importOpeningBalance({
				variables: {
					outletID: outletID,
					data: parsedData,
					mode: importMode,
				},
			});

			// Check if there are errors from the backend
			if (result.data?.importOpeningBalance?.errors && result.data.importOpeningBalance.errors.length > 0) {
				// Set validation errors and keep dialog open
				setValidationErrors(result.data.importOpeningBalance.errors);
				setOpenSnackBar(true);
				setSnackBarMsg(result.data.importOpeningBalance.message || 'Import completed with errors. Please fix the errors and try again.');
				setIsImporting(false);
				// Don't close dialog - let user see the errors
			} else if (!result.data?.importOpeningBalance?.success) {
				// Import failed but no specific errors
				setOpenSnackBar(true);
				setSnackBarMsg(result.data?.importOpeningBalance?.message || `Failed to import ${importMode === StockImportMode.OpeningBalance ? 'opening balance' : importMode === StockImportMode.StockIn ? 'stock in' : 'stock out'}`);
				setIsImporting(false);
				// Don't close dialog on failure
			} else {
				// Success - no errors
				setOpenSnackBar(true);
				setSnackBarMsg(SystemMsgs.createNewRecord());
				setIsImporting(false);
				// Close dialog only on successful import
				handleClose();
			}
		} catch (error: any) {
			setOpenSnackBar(true);
			setSnackBarMsg(error.message || `Failed to import ${importMode === StockImportMode.OpeningBalance ? 'opening balance' : importMode === StockImportMode.StockIn ? 'stock in' : 'stock out'}`);
			setIsImporting(false);
		}
	};

	return (
		<CommonDialog
			fullWidth
			open={open}
			onClose={handleClose}
			sections={{
                header: {
                    dynamic: (
                        <div>
                            <div className="dialogo-dynamic-content">
                                <span
                                    className="title c-orange flex-space"
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                     {importMode === StockImportMode.OpeningBalance ? 'Opening Balance' : importMode === StockImportMode.StockIn ? 'Stock In' : 'Stock Out'} Import
                                </span>
                            </div>
                        </div>
                    ),
                },
				body: () => (
					<>
						{/* Import Mode Selection */}
						<div style={{ marginBottom: '18px' }}>
							<Typography variant="caption" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
								Import Mode
							</Typography>
							<FormControl fullWidth style={{ marginBottom: '16px' }}>
								<InputLabel>Stock transaction type</InputLabel>
								<Select
									value={importMode}
									onChange={(e) => setImportMode(e.target.value as StockImportMode)}
									disabled={isImporting || isValidating}
								>
									<MenuItem value={StockImportMode.OpeningBalance}> Opening Balance (One time only)</MenuItem>
									<MenuItem value={StockImportMode.StockIn}> Stock In</MenuItem>
									<MenuItem value={StockImportMode.StockOut}> Stock Out</MenuItem>
								</Select>
							</FormControl>
						</div>

						<Divider style={{ margin: '18px 0' }} />

						{/* Download Template Section */}
						<div style={{ marginBottom: '18px' }}>
							<Typography variant="caption" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
								Download Template
							</Typography>
							<Button
								variant="outlined"
								color="primary"
								startIcon={<CloudDownload />}
								onClick={handleDownloadTemplate}
								fullWidth
							>
								Download {importMode === StockImportMode.OpeningBalance ? 'Opening Balance' : importMode === StockImportMode.StockIn ? 'Stock In' : 'Stock Out'} Template
							</Button>
						</div>

						<Divider style={{ margin: '18px 0' }} />

						{/* Upload Section */}
						<div>
							<Typography variant="caption" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
								Import Stock Data
							</Typography>

							{hasImported && importMode === StockImportMode.OpeningBalance && (
								<div style={{ 
									marginBottom: '12px', 
									padding: '12px', 
									backgroundColor: '#fff3cd', 
									border: '1px solid #ffc107',
									borderRadius: '4px',
									color: '#856404'
								}}>
									<Typography variant="body2" style={{ fontWeight: 'bold' }}>
										⚠️ Opening balance has already been imported for this outlet.
									</Typography>
									<Typography variant="body2" style={{ marginTop: '4px' }}>
										Each outlet can only import opening balance once.
									</Typography>
								</div>
							)}
							<InputUpload
								accept=".xlsx,.xls,.csv"
								value={uploadFile}
								label="Select Excel File"
								required
								handleUpload={handleFileSelect}
								inputRef={fileInputRef}
								disabled={hasImported && importMode === StockImportMode.OpeningBalance}
							/>

							{/* Validation Errors */}
							{validationErrors.length > 0 && (
								<div style={{ marginTop: '' }}>
									<Typography variant="subtitle2" style={{ color: '#f44336', marginBottom: '8px' }}>
										Validation Errors ({validationErrors.length}):
									</Typography>
									<List dense style={{ maxHeight: '200px', overflow: 'auto', backgroundColor: '#ffebee' }}>
										{validationErrors.map((error, idx) => (
											<React.Fragment key={idx}>
												<ListItem>
													<ListItemIcon>
														<ErrorIcon style={{ color: '#f44336' }} />
													</ListItemIcon>
													<ListItemText
														primary={`Row ${error.row}`}
														secondary={error.errors.join(', ')}
													/>
												</ListItem>
												{idx < validationErrors.length - 1 && <Divider />}
											</React.Fragment>
										))}
									</List>
								</div>
							)}

							{/* Success Message */}
							{validationErrors.length === 0 && parsedData.length > 0 && (
								<div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', color: '#4caf50' }}>
									<CheckCircle style={{ marginRight: '8px' }} />
									<Typography variant="body2">
										{parsedData.length} row(s) validated successfully
									</Typography>
								</div>
							)}
						</div>
					</>
				),
				footer: {
					actions: [
						{
							displayText: 'Cancel',
							props: {
								onClick: handleClose,
								variant: 'outlined',
								color: 'inherit',
							},
						},
						{
							displayText: 'Import',
							props: {
								onClick: handleImport,
								variant: 'contained',
								color: 'primary',
								disabled: (hasImported && importMode === StockImportMode.OpeningBalance) || !uploadFile || validationErrors.length > 0 || parsedData.length === 0 || isImporting || isValidating || importStatusLoading,
							},
						},
					],
				},
			}}
		/>
	);
};

